import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { AccessPolicyService } from '../services/access-policy.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { permittedLandingPath } from './dashboard-access.guard';

/**
 * Route guards for the Speed Budget & Forecast Engine (Phase 1). The engine is dark-launched behind
 * the {@code BUDGET_FORECAST} feature flag: each guard requires BOTH the flag to be enabled for the
 * current context AND the caller's capability, redirecting to the permitted landing page otherwise.
 */

/** Guards the account -> analysis-code mapping admin screen ({@code analysisCode.manage}). */
export const analysisCodeManageGuard: CanActivateFn = () => {
  const accessPolicy = inject(AccessPolicyService);
  const router = inject(Router);
  const featureFlagService = inject(FeatureFlagService);

  return featureFlagService.getCurrentFlags().pipe(
    map((flags) => flags['BUDGET_FORECAST'] === true && accessPolicy.canManageAnalysisCodes()
      ? true
      : router.createUrlTree([permittedLandingPath(accessPolicy)])),
    catchError(() => of(router.createUrlTree([permittedLandingPath(accessPolicy)])))
  );
};

/** Guards the read-only baseline view ({@code forecast.view}, implied by {@code budget.manage}). */
export const forecastViewGuard: CanActivateFn = () => {
  const accessPolicy = inject(AccessPolicyService);
  const router = inject(Router);
  const featureFlagService = inject(FeatureFlagService);

  return featureFlagService.getCurrentFlags().pipe(
    map((flags) => flags['BUDGET_FORECAST'] === true && accessPolicy.canViewForecast()
      ? true
      : router.createUrlTree([permittedLandingPath(accessPolicy)])),
    catchError(() => of(router.createUrlTree([permittedLandingPath(accessPolicy)])))
  );
};
